/* Collection browsing: real pagination, with search + player filters that
   reach across the whole collection.

   The previous version auto-appended other pages on every load by scraping the
   pager for links. The pager only ever links to three or four pages, so it
   produced an inconsistent grid, repeated products when you paged forward, and
   then hid the pager entirely — stranding every page it hadn't linked to. */
(() => {
  const init = (root) => {
    const input = root.querySelector('[data-collection-search]');
    const buttons = [...root.querySelectorAll('[data-player-filter]')];
    const countEl = root.querySelector('[data-visible-count]');
    const empty = root.querySelector('[data-collection-empty]');
    const status = root.querySelector('[data-collection-status]');
    const grid = root.querySelector('[data-collection-grid]');
    const pagination = root.querySelector('.pagination');
    if (!grid) return;

    const TOTAL_PAGES = parseInt(grid.dataset.totalPages, 10) || 1;
    const TOTAL_COUNT = parseInt(grid.dataset.totalCount, 10) || 0;
    const BASE_URL = grid.dataset.collectionUrl || window.location.pathname;

    // Rendered by the server for THIS page. Never detached.
    const pageItems = [...grid.querySelectorAll('[data-product-item]')];
    // Pulled from the other pages. Only in the DOM while a filter is active.
    const extraItems = [];
    let everything = pageItems;

    let player = '';
    let loadPromise = null;

    const hasFilter = () => Boolean((input && input.value.trim()) || player);

    const render = () => {
      if (!hasFilter()) {
        // Plain browsing: detach the fetched products, restore the pager.
        extraItems.forEach((el) => el.remove());
        everything = pageItems;
        pageItems.forEach((el) => { el.hidden = false; });
        if (countEl) countEl.textContent = TOTAL_COUNT;
        if (empty) empty.hidden = true;
        if (pagination) pagination.hidden = false;
        return;
      }

      // A filter spans the whole collection, so the pager no longer applies.
      if (pagination) pagination.hidden = true;

      const query = input ? input.value.trim().toLowerCase() : '';
      let visible = 0;
      everything.forEach((item) => {
        const matchesSearch = !query || (item.dataset.search || '').includes(query);
        const counts = (item.dataset.playerCounts || '').split(' ');
        const matchesPlayer = !player || counts.includes(player);
        item.hidden = !(matchesSearch && matchesPlayer);
        if (!item.hidden) visible += 1;
      });
      if (countEl) countEl.textContent = visible;
      if (empty) empty.hidden = visible !== 0;
    };

    const pageUrl = (n) => {
      const url = new URL(BASE_URL, window.location.origin);
      const sort = new URLSearchParams(window.location.search).get('sort_by');
      if (sort) url.searchParams.set('sort_by', sort);
      url.searchParams.set('page', String(n));
      return url.toString();
    };

    const hrefOf = (el) => {
      const a = el.querySelector('.collection-shopping-card__image, .product-card__image');
      return a ? a.getAttribute('href') : null;
    };

    const setBusy = (on) => { buttons.forEach((b) => { b.disabled = on; }); };

    // Walk pages 1..TOTAL_PAGES by number. Scraping the pager misses most of them.
    const loadEverything = () => {
      if (loadPromise) return loadPromise;
      if (TOTAL_PAGES <= 1) { loadPromise = Promise.resolve(); return loadPromise; }

      const current = parseInt(grid.dataset.currentPage, 10) || 1;
      const wanted = [];
      for (let n = 1; n <= TOTAL_PAGES; n += 1) if (n !== current) wanted.push(n);

      const seen = new Set(pageItems.map(hrefOf).filter(Boolean));
      const collected = new Map();
      let done = 0;

      if (status) {
        status.hidden = false;
        status.textContent = 'Loading all ' + TOTAL_COUNT + ' products…';
      }
      setBusy(true);

      const fetchPage = async (n) => {
        try {
          const res = await fetch(pageUrl(n), { headers: { 'X-Requested-With': 'fetch' } });
          if (!res.ok) return;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          collected.set(n, [...doc.querySelectorAll('[data-product-item]')]);
        } catch (e) {
          /* a page that fails simply contributes nothing */
        } finally {
          done += 1;
          if (status) {
            status.textContent = 'Loading all ' + TOTAL_COUNT + ' products… ('
              + Math.round((done / wanted.length) * 100) + '%)';
          }
        }
      };

      const queue = wanted.slice();
      const worker = async () => { while (queue.length) await fetchPage(queue.shift()); };
      const lanes = Math.min(6, wanted.length); // fast, without 27 sockets at once

      const finish = () => {
        if (status) status.hidden = true;
        setBusy(false);
      };

      loadPromise = Promise.all(Array.from({ length: lanes }, worker))
        .then(() => {
          // Re-assemble in page order so the chosen sort is preserved.
          [...collected.keys()].sort((a, b) => a - b).forEach((n) => {
            collected.get(n).forEach((node) => {
              const href = hrefOf(node);
              if (!href || seen.has(href)) return;
              seen.add(href);
              extraItems.push(document.importNode(node, true));
            });
          });
          finish();
        })
        .catch(finish);

      return loadPromise;
    };

    const update = async () => {
      if (hasFilter()) {
        await loadEverything();
        extraItems.forEach((el) => { if (!el.isConnected) grid.appendChild(el); });
        everything = pageItems.concat(extraItems);
      }
      render();
    };

    let debounce;
    if (input) {
      input.addEventListener('input', () => {
        clearTimeout(debounce);
        debounce = setTimeout(update, 180);
      });
    }

    buttons.forEach((button) => button.addEventListener('click', () => {
      player = button.dataset.playerFilter;
      buttons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      update();
    }));

    render();
  };

  document.querySelectorAll('[data-collection-tools]').forEach(init);
})();
